import { IBandFrequency } from '@components/ModalComponents/CellForm/CellForm';

import { getDateWithoutOffset } from '@utils/kpi-utils/date';
import { splitStringByCamelCase } from '@utils/strings/strings';

import { IAssetFile, IAssetSubTypeName, IAssetTypeName } from '@interfaces/assets/asset';
import { IBandOption } from '@interfaces/cells/cells';
import { IType } from '@interfaces/cmModule/dto/cmModuleDTO';
import {
	IColor,
	IId,
	IIdTitle,
	IIsFixed,
	IName,
	ISystemId,
	ITitle,
	ITooltipText,
	IValueLabel,
	TIdName,
	TNameTitle,
	TNameTitleValueLabel,
	TValueLabelName,
} from '@interfaces/common/common';
import { IIsAvailable } from '@interfaces/common/defaultDTO';
import { IStartEnd, TRange } from '@interfaces/common/range';
import { IItemsResponse } from '@interfaces/common/response';
import { TSelectOption } from '@interfaces/components/componentTypes';
import { IActionMenu } from '@interfaces/components/table';
import { ISiteDropdown } from '@interfaces/crowdcell/linkSite';
import { IError } from '@interfaces/errors/apiErrors';
import { IValidationRuleFolderDTO } from '@interfaces/networkElementConfigurations/dto/networkElementConfigurationsDTO';
import { IChangeMonitoringResponse } from '@interfaces/networkElementConfigurations/networkElementConfigurations';
import { IApprovalUser } from '@interfaces/organisation/approvalUnits';
import { ICategory, IOrganisationEntityFormatted } from '@interfaces/organisation/organisations';
import { IPortalAvailableMilestonesDTO, IPortalTaskToLinkDTO } from '@interfaces/portal/dto/taskManagementDTO';
import { IPortalFilesMilestoneToLink, IPortalMilestonesForAttachOption } from '@interfaces/portal/folders';
import { IProjectUser, IProjectWorkflow } from '@interfaces/portal/projects';
import { IUserEntity, TRole } from '@interfaces/user/user';

import { ESortDirection, TSortDirection, TTechnologiesValues } from '@enums/common';

import {
	adminRoles,
	doubleRoleTooltip,
	fixedForFirstAdminRoleTooltip,
	notAvailableRoleTooltip,
} from '@components/ModalComponents/UserForm/constants';

type TValueSystemId = IId & ISystemId;
type TValueLabelId = IType<TNameTitle> & IIsAvailable;
type TValueLabelAssetTypeName = IId & IAssetTypeName;
type TIsFixedTooltipText = ITooltipText & IIsFixed
type TSelectOptionWithTooltip = TSelectOption & Partial<TIsFixedTooltipText>;
type TNameWithTitleColor = TNameTitle & IColor
type TValueLabelAssetSubTypeName = IId & IAssetSubTypeName;

export const countriesFormatted = <T extends TIdName, >(countries: Array<T>): T[] => countries.map(country =>
	({ ...country, value: country.id, label: country.name }));

// eslint-disable-next-line max-len
export const organisationCategoriesFormatted = (categories: Array<ICategory>): Array<IOrganisationEntityFormatted> => {
	return categories.map(category => ({ ...category, value: category.id, label: category.name }));
};

export const nameTitleToOptionFormatted = (types: Array<TNameTitle>): (TNameTitle & IValueLabel)[] => types.map(type =>
	({ ...type, value: type.name, label: type.title }));

export const rolesFormatted = (roles: TRole[]): TRole[] => roles.map(role =>
	({
		...role,
		value: role.name,
		label: role.title,
	}));

export const usersFormatted = (
	organizationsUsersData: Array<IUserEntity>,
): TNameTitleValueLabel[] => organizationsUsersData.map((user: IUserEntity) => {
	return {
		value: `${user.firstName} ${user.lastName}`,
		name: user.id,
		title: `${user.firstName} ${user.lastName}`,
		label: `${user.firstName} ${user.lastName}`,
	};
});

export const projectUsersFormatted = (
	projectUsersData: Array<IProjectUser> | undefined,
): TNameTitleValueLabel[] => {
	if (!projectUsersData) {
		return [];
	}

	return projectUsersData?.map((user: IProjectUser) => {
		return {
			value: user.id,
			name: user.id,
			title: user.fullName,
			label: user.fullName,
		};
	});
};

export const portalTasksToLinkFormatted = (
	tasks: Array<IPortalTaskToLinkDTO>,
): Array<TSelectOption & IPortalTaskToLinkDTO> => tasks?.map((task: IPortalTaskToLinkDTO) => {
	return {
		...task,
		value: task.id,
		name: task.title,
		label: `${task.taskId} ${task.title}`,
	};
});

export const portalMilestonesToLinkFormatted = (
	milestones: Array<IPortalFilesMilestoneToLink>,
): Array<IPortalMilestonesForAttachOption> => milestones?.map((milestone: IPortalFilesMilestoneToLink) => {
	return {
		value: milestone.id,
		label: milestone.title,
		checked: milestone.isLinked,
	};
});

export const portalAvailableMilestonesFormatted = (
	milestones: Array<IPortalAvailableMilestonesDTO>,
): Array<TSelectOption & IPortalAvailableMilestonesDTO> => {
	return milestones?.map((milestone: IPortalAvailableMilestonesDTO) => {
		return {
			...milestone,
			value: milestone.id,
			label: milestone.title,
		};
	});
};

export const usersApprovalFormatted = (approvalUsers: Array<IApprovalUser>) =>
	approvalUsers.map((user: any) => {
		return {
			value: user.id,
			name: user.id,
			title: `${user.firstName} ${user.lastName}`,
			label: `${user.firstName} ${user.lastName}`,
		};
	});

export const actionsFormatted = (actions: Array<TNameTitle>): TNameTitleValueLabel[] => actions.map((action) => {
	return {
		value: action.name,
		name: action.name,
		title: action.title,
		label: action.title,
	};
});

export const rolesToCreateFormatted = (
	roles: Array<TSelectOption<string>>,
	isExistUserAdmin: boolean,
	isSomeOrganisationTypes: boolean,
): TSelectOptionWithTooltip[] => roles.map(role => {
	const fixedParams = (
		role?.name && adminRoles.map(({ role }) => role).includes(role.name)
	) ? (
			!isExistUserAdmin
				? {
					isFixed: true,
					tooltipText: fixedForFirstAdminRoleTooltip,
				}
				: isSomeOrganisationTypes
					? {
						tooltipText: doubleRoleTooltip,
					}
					: undefined
		)
		: undefined;

	return ({
		...role,
		value: role?.name,
		label: role?.title,
		...fixedParams,
	});
});

export const notAvailableRolesToCreateFormatted = (
	roles: Array<TSelectOption>,
): TSelectOptionWithTooltip[] => roles.map(role => {
	return ({
		...role,
		value: role?.name,
		label: role?.title,
		isFixed: role?.isFixed,
		tooltipText: role?.isFixed ? notAvailableRoleTooltip : undefined,
	});
});

export const categoriesFormatted = (categories: Array<ICategory>): IName[] => {
	return categories?.filter((category: ICategory) => category?.name)
		.map((category: ICategory) => ({ name: category.name }));
};

export const optionsFormatted = <T extends TNameWithTitleColor>(items: T[]) =>
	items?.map((item) => {
		return ({
			...item,
			value: item.name,
			label: item.title,
			hoverColor: item?.color ? item.color : undefined,
		});
	});

export const workflowsOptionsFormatted = (workflows: IProjectWorkflow[]) =>
	workflows?.map((item) => {
		return ({
			...item,
			value: item.name,
			label: `${item.title} (${item.statuses})`,
		});
	});

export const bandOptionsFormatted = <T extends TNameTitle & IBandFrequency>(
	items: T[],
	technology: TTechnologiesValues,
): IBandOption[] =>
	items?.map((item) => ({
		...item,
		value: item.name,
		label: `${item.title} (${item.frequencyRange.from} - ${item.frequencyRange.to})`,
		technology,
	}));

export const fieldsOptionsFormatted = (fields: Array<string>): IValueLabel[] => fields?.map((field: string) => ({
	value: field,
	label: field,
}));

export const optionsFormattedByName = <T extends Record<string, any>, >(
	fields: T[] = [],
): ((T & IValueLabel))[] => fields?.map((field: T) => ({
	...field,
	value: field.name,
	label: field.name,
}));

export const optionsFormattedByTitleName = (data: Array<TNameTitle>, sorting?: boolean) => {
	if (sorting) {
		data = [...data].sort((a, b) => a.name.localeCompare(b.name));
	}

	return data?.map((item) => ({
		value: item.name,
		label: item.title,
	}));
};

export const optionsFormattedByTitle = (fields: Array<ITitle>): IValueLabel[] => fields?.map((field) => ({
	...field,
	value: field.title,
	label: field.title,
}));

export const optionsFormattedWithId = <T extends IIdTitle>(items: T[]): (T & TValueLabelName)[] =>
	items?.map((item) => ({
		...item,
		value: item.id,
		name: item.id,
		label: item.title,
	}));

export const optionsFormattedWithIdName = <T extends TIdName>(items: T[] = []): (T & TValueLabelName)[] =>
	items?.map((item) => ({
		...item,
		value: item.id,
		name: item.id,
		label: item.name,
	}));

export const formattedToNumber = (value?: string): number | null => value ? +value : null;

export const errorsFormatter = (errors: Array<IError>) =>
	errors.reduce<Record<string, any>>((acc, error) => {
		acc[error.code] = { ...error };

		return acc;
	}, {});

export const getFileIds = (files?: Array<IAssetFile>): string[] | null => {
	if (files?.length) {
		return files.map(file => file.id);
	}

	return null;
};

export const optionsFormattedById = (items: Array<IId>): IValueLabel[] =>
	items.map(({ id }) => ({ label: id, value: id }));

export const optionsFormattedBySystemId = <T extends TValueSystemId>(
	items: T[],
): TSelectOption[] => {
	return items.map(({ id, systemId }) => ({ label: systemId, value: id, name: systemId }));
};

export const optionsFormattedFromEnum = <T extends string, >(enumObj: Record<T, string>): TSelectOption[] => {
	return Object.entries<string>(enumObj).map(([key, value]) => {
		return {
			value,
			name: value,
			label: splitStringByCamelCase(key),
		};
	});
};

export const requestCartPickedDatesFormatted = (items: Array<TRange<number>>): IStartEnd[] => {
	return items?.map(({ from, to }) => ({
		start: getDateWithoutOffset(from),
		end: getDateWithoutOffset(to),
	}));
};

export const optionsFormattedForSites = (items: ISiteDropdown[]) => {
	return items?.map((item) => {
		const { id, siteId, technologies, vendor } = item;

		return {
			label: `${siteId} ${technologies
				? `(${technologies?.map((technology) => technology.title).join('/')})`
				: ''}`,
			value: id,
			subText: vendor.title,
		};
	});
};

export const optionsFormattedWithAvailability = <T extends TValueLabelId>(
	items: T[],
): TSelectOption[] => {
	return items
		.filter((item) => item.isAvailable)
		.map(({ type }) => {
			return {
				value: type.name,
				label: type.title,
			};
		});
};

export const formatCurrency = (value: number): string => {
	const integerPart = Math.floor(value).toLocaleString('en-GB');
	const decimalPart = (value % 1);
	const formattedDecimal = decimalPart ? `.${decimalPart.toFixed(2).slice(2)}` : '';

	return `£${integerPart}${formattedDecimal}`;
};

export const optionsFormattedAssetType = <T extends TValueLabelAssetTypeName>(items: T[]): (T & IValueLabel)[] =>
	items?.map((item) => {
		return ({
			...item,
			value: item.id,
			label: item.assetTypeName,
		});
	});

export const optionsFormattedAssetSubType = <T extends TValueLabelAssetSubTypeName>(
	items: T[],
): (T & IValueLabel)[] =>
	items?.map((item) => {
		return ({
			...item,
			value: item.id,
			label: item.assetSubTypeName,
		});
	});

export const menuOptionsFormatted = <T, >(items: IActionMenu<T>[]): TSelectOption<T>[] => items?.map((item) => {
	return {
		value: item.id,
		label: item.text,
		disabled: item.disabled,
	};
});

export function optionsFormattedWithArray<T extends number | string, >(items: T[]): TSelectOption<T>[] {
	if (!items) return [];

	return items
		.map(item => ({
				value: item,
				label: item,
				name: item.toString(),
			}),
		);
}

interface MappedObject {
	value: string;
	label: string;

	[key: string]: any;
}

type NestedKeyOf<T> = T extends object
	? {
		[K in keyof T & string]: T[K] extends object
			? T[K] extends ((...args: any) => any) | Array<any> | Date | RegExp
				? K
				: `${K}.${NestedKeyOf<T[K]>}` | K
			: K;
	}[keyof T & string]
	: never;

interface MapDropdownOptionsParams<T> {
	items: T[];
	valueKey: NestedKeyOf<T>;
	labelKey: NestedKeyOf<T>;
	additionalMappings?: { [key: string]: NestedKeyOf<T> };
	preserveZeroValue?: boolean;
	sort?: TSortDirection;
}

interface NestedObject {
	[key: string]: string | number | boolean | null | undefined | NestedObject | NestedObject[];
}

export const getNestedValue = (obj: NestedObject, path: string): string => {
	if (!path.includes('.')) {
		const value = obj[path];

		return value !== undefined && value !== null ? String(value) : '';
	}

	return path.split('.').reduce<any>((acc, part, index, parts) => {
		if (index === parts.length - 1) {
			if (acc && typeof acc === 'object' && acc !== null && part in acc) {
				const value = acc[part];

				return value !== undefined && value !== null ? String(value) : '';
			}

			return '';
		}

		if (acc && typeof acc === 'object' && acc !== null && part in acc) {
			return acc[part];
		}

		return null;
	}, obj);
};

export function mapDropdownOptions<T>({
	items,
	valueKey,
	labelKey,
	additionalMappings,
	sort,
}: MapDropdownOptionsParams<T>): MappedObject[] {
	let sortedItems = items;

	if (sort === ESortDirection.asc) {
		sortedItems = [...items].sort((a, b) => {
			const labelA = getNestedValue(a as unknown as NestedObject, labelKey as string);
			const labelB = getNestedValue(b as unknown as NestedObject, labelKey as string);

			return labelA.localeCompare(labelB);
		});
	} else if (sort === ESortDirection.desc) {
		sortedItems = [...items].sort((a, b) => {
			const labelA = getNestedValue(a as unknown as NestedObject, labelKey as string);
			const labelB = getNestedValue(b as unknown as NestedObject, labelKey as string);

			return labelB.localeCompare(labelA);
		});
	}

	return sortedItems.map(item => {
		if (!item) {
			return {
				value: '',
				label: '',
			};
		}

		const value = getNestedValue(item as unknown as NestedObject, valueKey as string);
		const label = getNestedValue(item as unknown as NestedObject, labelKey as string);

		const mappedItem: MappedObject = {
			value,
			label,
		};

		if (additionalMappings) {
			Object.keys(additionalMappings).forEach(newKey => {
				const originalKey = additionalMappings[newKey];

				mappedItem[newKey] = getNestedValue(item as unknown as NestedObject, originalKey as string) || '';
			});
		}

		return mappedItem;
	});
}

export const validationIdToOptionsFormatter = (items: IValidationRuleFolderDTO[]): TSelectOption[] => {
	return items.map((item) => {
		return {
			...item,
			value: item.validationRulesFolderId,
			title: item.name,
			label: item.name,
			id: item.validationRulesFolderId,
		};
	});
};

export const formatChangeMonitoringItems = (data: IItemsResponse<IChangeMonitoringResponse>) => {
	return {
		items: data.items.map((item: IChangeMonitoringResponse) => item.items).flat(),
		count: data.count,
	};
};

export const decodeHtmlEntity = (value: string) => {
	return new DOMParser()
		.parseFromString(value, 'text/html')
		.documentElement.textContent;
};
